import { ExecutionsLimitReachedError, InstancesCountLimitReachedError, NotifiesLimitReachedError } from './errors';
import { reportError } from '../utils';

export const RATE_LIMITS = {
    /**
     * Допустимое количество одновременно выполняемых запусков экземпляра реакции.
     */
    maxExecutionsCount: 100,
    /**
     * Допустимое количество выполненных/выполняемых запусков экземпляра реакции за временной интервал.
     */
    maxExecutionsCountInInterval: 100,
    /**
     * Допустимое количество нотификаций об изменении источников в рамках батча.
     * Очищается при выполнении отложенного запуска
     */
    maxNotificationsCount: 20,
    /**
     * Допустимое количество одновременно отложенных запусков экземпляра реакции.
     */
    maxNotificationsCountInInterval: 100,
    /**
     * Допустимое количество одновременно существующих экземпляров реакции.
     * Превышение количества может говорить об отсутствии отписки / запуске реакции для большого количества разных параметров
     */
    maxInstancesCount: 100,
    /**
     * Временной интервал проверки лимитов (ms)
     */
    checkInterval: 1000,
} as const;

export class ReactionMonitoring {
    private scheduleLog: number[] = [];
    private executionLog: number[] = [];
    private errorInstance: Error;
    private scheduleLastCheckTimestamp: number = Date.now();
    private executionLastCheckTimestamp: number = Date.now();

    constructor(errorInstance: Error) {
        this.errorInstance = errorInstance;
    }

    public onInstanceCreate(instancesCount: number) {
        if (instancesCount > RATE_LIMITS.maxInstancesCount) {
            reportError(
                new InstancesCountLimitReachedError('Превышен лимит количества существующих экземпляров реакций', {
                    cause: this.errorInstance,
                    additional: {
                        instancesCount,
                    },
                })
            );
        }
    }

    public onSchedule(executionsCount: number) {
        if (executionsCount > RATE_LIMITS.maxExecutionsCount) {
            reportError(
                new ExecutionsLimitReachedError('Превышен лимит количества одновременных запусков экземпляра реакции', {
                    cause: this.errorInstance,
                    additional: {
                        executionsCount: executionsCount,
                    },
                })
            );
        }
    }

    public onExecute() {
        const dateNow = Date.now();
        this.executionLog.push(dateNow);

        // Проверяем превышение лимита не чаще RATE_LIMITS.checkInterval
        if (dateNow - this.executionLastCheckTimestamp >= RATE_LIMITS.checkInterval) {
            this.executionLastCheckTimestamp = dateNow;
            const windowStart = dateNow - RATE_LIMITS.checkInterval;
            const indexOfFirstInWindow = this.executionLog.findIndex(date => date >= windowStart);
            // Отбрасываем все записи, которые старше текущего временного окна
            this.executionLog.splice(0, indexOfFirstInWindow);

            if (this.executionLog.length > RATE_LIMITS.maxExecutionsCountInInterval) {
                reportError(
                    new ExecutionsLimitReachedError(
                        'Превышен лимит запусков экземпляра реакции за временной интервал',
                        {
                            cause: this.errorInstance,
                            additional: {
                                executionsCount: this.executionLog.length,
                            },
                        }
                    )
                );
            }
        }
    }

    public onNotify(notificationsCount: number) {
        if (notificationsCount > RATE_LIMITS.maxNotificationsCount) {
            reportError(
                new NotifiesLimitReachedError('Превышен лимит количества оповещений реакции об изменениях источников', {
                    cause: this.errorInstance,
                    additional: {
                        notificationsCount: notificationsCount,
                    },
                })
            );
        }

        const dateNow = Date.now();
        this.scheduleLog.push(dateNow);

        // Проверяем превышение лимита не чаще RATE_LIMITS.checkInterval
        if (dateNow - this.scheduleLastCheckTimestamp >= RATE_LIMITS.checkInterval) {
            this.scheduleLastCheckTimestamp = dateNow;
            const windowStart = dateNow - RATE_LIMITS.checkInterval;
            const indexOfFirstInWindow = this.scheduleLog.findIndex(date => date >= windowStart);
            // Отбрасываем все записи, которые старше текущего временного окна
            this.scheduleLog.splice(0, indexOfFirstInWindow);

            if (this.scheduleLog.length > RATE_LIMITS.maxNotificationsCountInInterval) {
                reportError(
                    new NotifiesLimitReachedError('Превышен лимит оповещений реакции за временной интервал', {
                        cause: this.errorInstance,
                        additional: {
                            notificationsCount: this.scheduleLog.length,
                        },
                    })
                );
            }
        }
    }
}
